<template>
  <div id="app">
    <router-view></router-view>
    <!-- 全局悬浮不占空间滚动条 -->
    <div
      v-show="showScrollbar"
      ref="track"
      class="global-floating-scrollbar"
      :class="{
        'is-active': isHovered || isDragging || isScrolling,
        dragging: isDragging
      }"
      @pointerdown="handleTrackPointerDown"
      @mouseenter="handleTrackMouseEnter"
      @mouseleave="handleTrackMouseLeave"
    >
      <div
        ref="thumb"
        class="scrollbar-thumb"
        :style="{
          height: thumbHeight + 'px',
          transform: 'translateY(' + thumbTop + 'px)'
        }"
        @pointerdown.stop="handleThumbPointerDown"
      ></div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'App',
  data() {
    return {
      showScrollbar: false,
      thumbHeight: 40,
      thumbTop: 0,
      isDragging: false,
      isHovered: false,
      isScrolling: false,
      scrollTimer: null,
      rafId: null,
      grabOffsetY: 0,
      activePointerId: null
    };
  },
  watch: {
    $route() {
      this.$nextTick(() => {
        this.updateScrollbar();
      });
    }
  },
  mounted() {
    this.updateScrollbar = this.updateScrollbar.bind(this);
    this.requestUpdate = this.requestUpdate.bind(this);
    this.handleScroll = this.handleScroll.bind(this);
    this.handlePointerMove = this.handlePointerMove.bind(this);
    this.handlePointerUp = this.handlePointerUp.bind(this);
    window.addEventListener('scroll', this.handleScroll, { passive: true });
    window.addEventListener('resize', this.requestUpdate, { passive: true });
    if (typeof ResizeObserver !== 'undefined') {
      this.observer = new ResizeObserver(() => {
        this.requestUpdate();
      });
      if (document.body) this.observer.observe(document.body);
      if (document.documentElement) this.observer.observe(document.documentElement);
    }
    this.$nextTick(() => {
      this.updateScrollbar();
    });
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.handleScroll);
    window.removeEventListener('resize', this.requestUpdate);
    window.removeEventListener('pointermove', this.handlePointerMove);
    window.removeEventListener('pointerup', this.handlePointerUp);
    window.removeEventListener('pointercancel', this.handlePointerUp);
    if (this.scrollTimer) {
      clearTimeout(this.scrollTimer);
      this.scrollTimer = null;
    }
    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
  },
  methods: {
    requestUpdate() {
      if (this.rafId) return;
      this.rafId = requestAnimationFrame(() => {
        this.updateScrollbar();
        this.rafId = null;
      });
    },
    handleScroll() {
      if (this.isDragging) return;
      this.requestUpdate();
      this.isScrolling = true;
      if (this.scrollTimer) {
        clearTimeout(this.scrollTimer);
      }
      this.scrollTimer = setTimeout(() => {
        this.isScrolling = false;
      }, 800);
    },
    handleTrackMouseEnter() {
      this.isHovered = true;
    },
    handleTrackMouseLeave() {
      if (!this.isDragging) {
        this.isHovered = false;
      }
    },
    handleTrackPointerDown(e) {
      if (e.button !== undefined && e.button !== 0) return;
      e.preventDefault();
      this.startDrag(e, true);
    },
    handleThumbPointerDown(e) {
      if (e.button !== undefined && e.button !== 0) return;
      e.preventDefault();
      this.startDrag(e, false);
    },
    startDrag(e, isTrackClick) {
      this.isDragging = true;
      this.isHovered = true;
      this.activePointerId = e.pointerId;

      const track = this.$refs.track;
      if (track && track.setPointerCapture && e.pointerId !== undefined) {
        try {
          track.setPointerCapture(e.pointerId);
        } catch (_) {}
      }

      const trackRect = track ? track.getBoundingClientRect() : { top: 0, height: window.innerHeight };
      const currentY = e.clientY - trackRect.top;

      const appEl = document.getElementById('app');
      const docHeight = Math.max(
        document.documentElement.scrollHeight,
        document.body.scrollHeight,
        appEl ? appEl.scrollHeight : 0
      );
      const winHeight = window.innerHeight;
      const maxScroll = docHeight - winHeight;
      const maxThumbTravel = winHeight - this.thumbHeight;

      if (isTrackClick) {
        // 手指/鼠标点击轨道：滑块中心直接对齐触碰点，并直接滚动至对应位置
        let targetThumbTop = currentY - this.thumbHeight / 2;
        targetThumbTop = Math.max(0, Math.min(maxThumbTravel, targetThumbTop));
        const targetScrollTop = maxThumbTravel > 0 ? (targetThumbTop / maxThumbTravel) * maxScroll : 0;

        window.scrollTo(0, targetScrollTop);
        this.thumbTop = targetThumbTop;
        this.grabOffsetY = this.thumbHeight / 2;
      } else {
        // 点击滑块本身：保留抓取偏置，避免滑块发生跳跃
        let offset = currentY - this.thumbTop;
        if (offset < 0 || offset > this.thumbHeight) {
          offset = this.thumbHeight / 2;
        }
        this.grabOffsetY = offset;
      }

      window.addEventListener('pointermove', this.handlePointerMove, { passive: false });
      window.addEventListener('pointerup', this.handlePointerUp, { passive: true });
      window.addEventListener('pointercancel', this.handlePointerUp, { passive: true });
    },
    handlePointerMove(e) {
      if (!this.isDragging) return;
      if (this.activePointerId !== null && e.pointerId !== undefined && e.pointerId !== this.activePointerId) {
        return;
      }
      e.preventDefault();

      const track = this.$refs.track;
      const trackRect = track ? track.getBoundingClientRect() : { top: 0, height: window.innerHeight };
      const currentY = e.clientY - trackRect.top;

      const appEl = document.getElementById('app');
      const docHeight = Math.max(
        document.documentElement.scrollHeight,
        document.body.scrollHeight,
        appEl ? appEl.scrollHeight : 0
      );
      const winHeight = window.innerHeight;
      const maxScroll = docHeight - winHeight;
      const maxThumbTravel = winHeight - this.thumbHeight;

      if (maxScroll <= 0 || maxThumbTravel <= 0) return;

      let targetThumbTop = currentY - this.grabOffsetY;
      targetThumbTop = Math.max(0, Math.min(maxThumbTravel, targetThumbTop));

      const targetScrollTop = (targetThumbTop / maxThumbTravel) * maxScroll;
      window.scrollTo(0, targetScrollTop);
      this.thumbTop = targetThumbTop;
    },
    handlePointerUp(e) {
      if (this.activePointerId !== null && e.pointerId !== undefined && e.pointerId !== this.activePointerId) {
        return;
      }
      this.isDragging = false;
      this.isHovered = false;

      const track = this.$refs.track;
      if (track && track.releasePointerCapture && this.activePointerId !== null && this.activePointerId !== undefined) {
        try {
          track.releasePointerCapture(this.activePointerId);
        } catch (_) {}
      }
      this.activePointerId = null;

      window.removeEventListener('pointermove', this.handlePointerMove);
      window.removeEventListener('pointerup', this.handlePointerUp);
      window.removeEventListener('pointercancel', this.handlePointerUp);

      this.updateScrollbar();
    },
    updateScrollbar() {
      const appEl = document.getElementById('app');
      const docHeight = Math.max(
        document.documentElement.scrollHeight,
        document.body.scrollHeight,
        appEl ? appEl.scrollHeight : 0
      );
      const winHeight = window.innerHeight;
      if (docHeight <= winHeight + 2) {
        this.showScrollbar = false;
        return;
      }
      this.showScrollbar = true;
      const minThumbHeight = 40;
      const calculatedHeight = (winHeight / docHeight) * winHeight;
      this.thumbHeight = Math.max(minThumbHeight, calculatedHeight);
      const maxScroll = docHeight - winHeight;
      const maxThumbTravel = winHeight - this.thumbHeight;
      const currentScroll = window.scrollY || document.documentElement.scrollTop || document.body.scrollTop || 0;
      this.thumbTop = maxScroll > 0 ? (currentScroll / maxScroll) * maxThumbTravel : 0;
    }
  }
}
</script>

<style>
.global-floating-scrollbar {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: 14px;
  z-index: 9999;
  pointer-events: auto;
  user-select: none;
  touch-action: none;
  background-color: transparent;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.global-floating-scrollbar:hover,
.global-floating-scrollbar.is-active,
.global-floating-scrollbar.dragging {
  background-color: rgba(255, 255, 255, 0.05);
}

.global-floating-scrollbar .scrollbar-thumb {
  position: absolute;
  right: 3px;
  width: 6px;
  border-radius: 6px;
  background-color: rgba(156, 163, 175, 0.55);
  box-shadow: 0 0 2px rgba(0, 0, 0, 0.25);
  cursor: pointer;
  pointer-events: auto;
  user-select: none;
  touch-action: none;
  opacity: 1; /* 滚动条常驻存在，不自动隐藏 */
  transition: background-color 0.15s ease, width 0.15s ease, right 0.15s ease;
}

.global-floating-scrollbar:hover .scrollbar-thumb,
.global-floating-scrollbar.is-active .scrollbar-thumb,
.global-floating-scrollbar.dragging .scrollbar-thumb {
  background-color: rgba(156, 163, 175, 0.9);
  width: 8px;
  right: 2px;
}
</style>
